import React, { useState, useEffect } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Pie, Bar } from 'react-chartjs-2';
import axios from 'axios';
import ls from 'localstorage-slim';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

import { refreshToken, getETAToken, updateUserLastLogin } from '../helpers';
import Loading from './Loading';

function Dashboard() {
  const subDomain = ls.get('subDomain');

  const [issuerStatusChartData, setIssuerStatusChartData] = useState([]);
  const [issuerTypeChartData, setIssuerTypeChartData] = useState([]);
  const [issuerCustomers, setIssuerCustomers] = useState([]);
  const [issuerInvoicelines, setIssuerInvoicelines] = useState([]);
  const [receiverStatusChartData, setReceiverStatusChartData] = useState([]);
  const [receiverTypeChartData, setReceiverTypeChartData] = useState([]);
  const [receiverCustomers, setReceiverCustomers] = useState([]);
  const [receiverInvoicelines, setReceiverInvoicelines] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const { t, i18n } = useTranslation();

  ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement);

  const typeChartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
      },
    },
    scales: {
      total: {
        type: 'linear',
        display: true,
        position: 'left',
      },
      count: {
        type: 'linear',
        display: true,
        position: 'right',
        grid: {
          drawOnChartArea: false,
        },
      },
    },
  };

  const statusChartOptions = {
    responsive: false,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
    },
  };

  const customerChartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
      },
    },
  };

  const issuerTypeChart = {
    labels: issuerTypeChartData.map(document =>
      document.document_type === 'i'
        ? 'Invoice'
        : document.document_type === 'd'
        ? 'Debit Note'
        : 'Credit Note'
    ),
    datasets: [
      {
        label: '# of Documents',
        data: issuerTypeChartData.map(document => document.count),
        backgroundColor: ['rgba(255, 99, 132, 0.2)'],
        borderColor: ['rgba(255, 99, 132, 1)'],
        borderWidth: 1,
        yAxisID: 'count',
      },
      {
        label: 'Total Amount',
        data: issuerTypeChartData.map(document => document.total_amount),
        backgroundColor: ['rgba(54, 162, 235, 0.2)'],
        borderColor: ['rgba(54, 162, 235, 1)'],
        borderWidth: 1,
        yAxisID: 'total',
      },
      {
        label: 'Total Tax Amount',
        data: issuerTypeChartData.map(document => document.total_tax_amount),
        backgroundColor: ['rgba(255, 206, 86, 0.2)'],
        borderColor: ['rgba(255, 206, 86, 1)'],
        borderWidth: 1,
        yAxisID: 'total',
      },
    ],
  };

  const receiverTypeChart = {
    labels: receiverTypeChartData.map(document =>
      document.document_type === 'i'
        ? 'Invoice'
        : document.document_type === 'd'
        ? 'Debit Note'
        : 'Credit Note'
    ),
    datasets: [
      {
        label: '# of Documents',
        data: receiverTypeChartData.map(document => document.count),
        backgroundColor: ['rgba(255, 99, 132, 0.2)'],
        borderColor: ['rgba(255, 99, 132, 1)'],
        borderWidth: 1,
        yAxisID: 'count',
      },
      {
        label: 'Total Amount',
        data: receiverTypeChartData.map(document => document.total_amount),
        backgroundColor: ['rgba(54, 162, 235, 0.2)'],
        borderColor: ['rgba(54, 162, 235, 1)'],
        borderWidth: 1,
        yAxisID: 'total',
      },
      {
        label: 'Total Tax Amount',
        data: receiverTypeChartData.map(document => document.total_tax_amount),
        backgroundColor: ['rgba(255, 206, 86, 0.2)'],
        borderColor: ['rgba(255, 206, 86, 1)'],
        borderWidth: 1,
        yAxisID: 'total',
      },
    ],
  };

  const issuerStatusChart = {
    labels: issuerStatusChartData
      .filter(document => document.document_status !== null)
      .map(invoiceline => invoiceline.document_status),
    datasets: [
      {
        label: '# of Documents',
        data: issuerStatusChartData
          .filter(document => document.document_status !== null)
          .map(document => document.count),
        backgroundColor: [
          'rgba(247, 58, 99, 0.2)',
          'rgba(8, 83, 83, 0.2)',
          'rgba(54, 162, 235, 0.2)',
          'rgba(255, 206, 86, 0.2)',
          'rgba(153, 102, 255, 0.2)',
          'rgba(255, 159, 64, 0.2)',
        ],
        borderColor: [
          'rgba(247, 58, 99, 1)',
          'rgba(8, 83, 83, 1)',
          'rgba(54, 162, 235, 1)',
          'rgba(255, 206, 86, 1)',
          'rgba(153, 102, 255, 1)',
          'rgba(255, 159, 64, 1)',
        ],
        borderWidth: 1,
      },
    ],
  };

  const receiverStatusChart = {
    labels: receiverStatusChartData
      .filter(document => document.document_status !== null)
      .map(invoiceline => invoiceline.document_status),
    datasets: [
      {
        label: '# of Documents',
        data: receiverStatusChartData
          .filter(document => document.document_status !== null)
          .map(document => document.count),
        backgroundColor: [
          'rgba(255, 99, 132, 0.2)',
          'rgba(54, 162, 235, 0.2)',
          'rgba(255, 206, 86, 0.2)',
          'rgba(75, 192, 192, 0.2)',
          'rgba(153, 102, 255, 0.2)',
          'rgba(255, 159, 64, 0.2)',
        ],
        borderColor: [
          'rgba(255, 99, 132, 1)',
          'rgba(54, 162, 235, 1)',
          'rgba(255, 206, 86, 1)',
          'rgba(75, 192, 192, 1)',
          'rgba(153, 102, 255, 1)',
          'rgba(255, 159, 64, 1)',
        ],
        borderWidth: 1,
      },
    ],
  };

  const issuerCustomersChart = {
    labels: issuerCustomers.map(document => document.company),
    datasets: [
      {
        label: '# of Documents',
        data: issuerCustomers.map(document => document.count),
        backgroundColor: [
          'rgba(247, 58, 99, 0.2)',
          'rgba(8, 83, 83, 0.2)',
          'rgba(54, 162, 235, 0.2)',
          'rgba(255, 206, 86, 0.2)',
          'rgba(153, 102, 255, 0.2)',
          'rgba(255, 159, 64, 0.2)',
        ],
        borderColor: [
          'rgba(247, 58, 99, 1)',
          'rgba(8, 83, 83, 1)',
          'rgba(54, 162, 235, 1)',
          'rgba(255, 206, 86, 1)',
          'rgba(153, 102, 255, 1)',
          'rgba(255, 159, 64, 1)',
        ],
        borderWidth: 1,
      },
    ],
  };

  const receiverCustomersChart = {
    labels: receiverCustomers.map(document => document.company),
    datasets: [
      {
        label: '# of Documents',
        data: receiverCustomers.map(document => document.count),
        backgroundColor: [
          'rgba(247, 58, 99, 0.2)',
          'rgba(8, 83, 83, 0.2)',
          'rgba(54, 162, 235, 0.2)',
          'rgba(255, 206, 86, 0.2)',
          'rgba(153, 102, 255, 0.2)',
          'rgba(255, 159, 64, 0.2)',
        ],
        borderColor: [
          'rgba(247, 58, 99, 1)',
          'rgba(8, 83, 83, 1)',
          'rgba(54, 162, 235, 1)',
          'rgba(255, 206, 86, 1)',
          'rgba(153, 102, 255, 1)',
          'rgba(255, 159, 64, 1)',
        ],
        borderWidth: 1,
      },
    ],
  };

  useEffect(() => {
    async function fetchData() {
      const dashboardToast = toast.loading('Please wait...');
      try {
        if (!ls.get('access')) {
          await refreshToken();
          await updateUserLastLogin();
        }
        if (!ls.get('ETAToken')) await getETAToken();

        let response = await axios.get(
          `${subDomain}${process.env.REACT_APP_DJANGO_BASEURL}/erp/dashboard_statistics`,
          {
            params: {
              taxpayer: ls.get('taxpayer_id'),
            },
          }
        );

        setIssuerStatusChartData(response.data.issuer_document_status_chart);
        setIssuerTypeChartData(response.data.issuer_document_type_chart);
        setIssuerCustomers(response.data.issuer_customers);
        setIssuerInvoicelines(response.data.issuer_invoicelines);
        setReceiverStatusChartData(response.data.receiver_document_status_chart);
        setReceiverTypeChartData(response.data.receiver_document_type_chart);
        setReceiverInvoicelines(response.data.receiver_invoicelines);
        setReceiverCustomers(response.data.receiver_customers);
        setIsLoading(false);

        toast.update(dashboardToast, {
          render: 'Codes retrieved successfully',
          type: 'success',
          isLoading: false,
          autoClose: 1000,
          theme: 'colored',
        });
      } catch (error) {
        if (error.response) {
          // Request made but the server responded with an error
          console.log(error.response.data);
          toast.update(dashboardToast, {
            render: `${error.response.data}`,
            type: 'error',
            isLoading: false,
            autoClose: 1000,
            theme: 'colored',
          });
        } else if (error.request) {
          // Request made but no response is received from the server.
          console.log(error.request);
          toast.update(dashboardToast, {
            render: `${error.request}`,
            type: 'error',
            isLoading: false,
            autoClose: 1000,
            theme: 'colored',
          });
        } else {
          // Error occured while setting up the request
          console.log('Error', error.message);
          toast.update(dashboardToast, {
            render: `${error.message}`,
            type: 'error',
            isLoading: false,
            autoClose: 1000,
            theme: 'colored',
          });
        }
      }
    }

    fetchData();
  }, []);

  return isLoading ? (
    <div style={{ height: '77vh' }}>
      <Loading />
    </div>
  ) : (
    <>
      <div
        className={`row text-center row-cols-1 row-cols-sm-2 gx-5 gy-5 justify-content-center mx-0`}>
        <div className='col p-4 shadow-sm'>
          <div className={`row mb-2 p-2`}>
            <span
              className={`subtitle text-decoration-underline pb-2 ${
                i18n.language === 'en' ? '' : 'text-end'
              }`}
              style={{ textUnderlineOffset: '10px' }}>
              <strong className='fs-5'>{t('homePage.dashboardPage.submitted')}</strong>
            </span>
          </div>
          <Bar
            data={issuerTypeChart}
            options={typeChartOptions}
            className={`${issuerTypeChartData.length === 0 ? 'd-none' : ''}`}
          />
          {issuerTypeChartData.length === 0 && (
            <div className='d-flex justify-content-center align-items-center h-75'>
              <span className='fw-bold fs-4'>{t('homePage.noStates')}</span>
            </div>
          )}
        </div>
        <div className='col p-4 shadow-sm'>
          <div className='row mb-2 p-2'>
            <span
              className={`subtitle text-decoration-underline pb-2 ${
                i18n.language === 'en' ? '' : 'text-end'
              }`}
              style={{ textUnderlineOffset: '10px' }}>
              <strong className='fs-5'>{t('homePage.dashboardPage.received')}</strong>
            </span>
          </div>
          <Bar
            data={receiverTypeChart}
            options={typeChartOptions}
            className={`${receiverTypeChartData.length === 0 ? 'd-none' : ''}`}
          />
          {receiverTypeChartData.length === 0 && (
            <div className='d-flex justify-content-center align-items-center h-75'>
              <span className='fw-bold fs-4'>{t('homePage.noStates')}</span>
            </div>
          )}
        </div>

        <div className='col p-4 shadow-sm'>
          <div className='row mb-2 p-2'>
            <span
              className={`subtitle text-decoration-underline pb-2 ${
                i18n.language === 'en' ? '' : 'text-end'
              }`}
              style={{ textUnderlineOffset: '10px' }}>
              <strong className='fs-5'>{t('homePage.dashboardPage.states')}</strong>
            </span>
          </div>
          <Pie
            options={statusChartOptions}
            data={issuerStatusChart}
            width={550}
            height={275}
            className={`${
              issuerStatusChartData.filter(invoiceline => invoiceline.document_status !== null)
                .length === 0
                ? 'd-none'
                : ''
            }`}
          />
          {issuerStatusChartData.filter(invoiceline => invoiceline.document_status !== null)
            .length === 0 && (
            <div className='d-flex justify-content-center align-items-center h-75'>
              <span className='fw-bold fs-4'>{t('homePage.dashboardPage.submitted')}</span>
            </div>
          )}
        </div>
        <div className='col p-4 shadow-sm'>
          <div className='row mb-2 p-2'>
            <span
              className={`subtitle text-decoration-underline pb-2 ${
                i18n.language === 'en' ? '' : 'text-end'
              }`}
              style={{ textUnderlineOffset: '10px' }}>
              <strong className='fs-5'>{t('homePage.dashboardPage.states')}</strong>
            </span>
          </div>
          <Pie
            options={statusChartOptions}
            data={receiverStatusChart}
            width={550}
            height={275}
            className={`${
              receiverStatusChartData.filter(invoiceline => invoiceline.document_status !== null)
                .length === 0
                ? 'd-none'
                : ''
            }`}
          />
          {receiverStatusChartData.filter(invoiceline => invoiceline.document_status !== null)
            .length === 0 && (
            <div className='d-flex justify-content-center align-items-center h-75'>
              <span className='fw-bold fs-4'>{t('homePage.noStates')}</span>
            </div>
          )}
        </div>

        <div className='col p-4 shadow-sm'>
          <div className='row mb-2 p-2'>
            <span
              className={`subtitle text-decoration-underline pb-2 ${
                i18n.language === 'en' ? '' : 'text-end'
              }`}
              style={{ textUnderlineOffset: '10px' }}>
              <strong className='fs-5 text-capitalize'>
                {t('homePage.dashboardPage.issuerCustomer')}
              </strong>
            </span>
          </div>
          <Bar
            data={issuerCustomersChart}
            options={customerChartOptions}
            className={`${issuerCustomers.length === 0 ? 'd-none' : ''}`}
          />
          {issuerCustomers.length === 0 && (
            <div className='d-flex justify-content-center align-items-center h-75'>
              <span className='fw-bold fs-4'>{t('homePage.noStates')}</span>
            </div>
          )}
        </div>
        <div className='col p-4 shadow-sm'>
          <div className='row mb-2 p-2'>
            <span
              className={`subtitle text-decoration-underline pb-2 ${
                i18n.language === 'en' ? '' : 'text-end'
              }`}
              style={{ textUnderlineOffset: '10px' }}>
              <strong className='fs-5 text-capitalize'>
                {t('homePage.dashboardPage.receiverCustomer')}
              </strong>
            </span>
          </div>
          <Bar
            data={receiverCustomersChart}
            options={customerChartOptions}
            className={`${receiverCustomers.length === 0 ? 'd-none' : ''}`}
          />
          {receiverCustomers.length === 0 && (
            <div className='d-flex justify-content-center align-items-center h-75'>
              <span className='fw-bold fs-4'>{t('homePage.noStates')}</span>
            </div>
          )}
        </div>

        <div className='col p-4 shadow-sm'>
          <div className='row mb-2 p-2'>
            <span
              className={`subtitle text-decoration-underline pb-2 ${
                i18n.language === 'en' ? '' : 'text-end'
              }`}
              style={{ textUnderlineOffset: '10px' }}>
              <strong className='fs-5'>{t('homePage.dashboardPage.goods')}</strong>
            </span>
          </div>
          <div className='row'>
            <table className='table col mt-2' dir={i18n.language === 'en' ? 'ltr' : 'rtl'}>
              <thead
                style={{
                  verticalAlign: 'middle',
                }}>
                <tr>
                  <th scope='col-4'>{t('homePage.dashboardPage.table.description')}</th>
                  <th scope='col-2'>{t('homePage.dashboardPage.table.count')}</th>
                  <th scope='col-3 text-nowrap'>{t('homePage.dashboardPage.table.total')}</th>
                  <th scope='col-3 text-nowrap'>{t('homePage.dashboardPage.table.tax')}</th>
                </tr>
              </thead>
              <tbody>
                {issuerInvoicelines.map(invoiceline => (
                  <tr key={invoiceline.id}>
                    <td className='col-4' dir='ltr'>
                      <span className='d-block fw-bold dashboard__truncatedText'>
                        {invoiceline.description}
                      </span>
                      <span className='d-block'>{invoiceline.item_code}</span>
                    </td>
                    <td className='col-2'>
                      <span className='d-block fw-bold'>{invoiceline.count}</span>
                    </td>
                    <td className='col-3'>
                      <div className='d-block'>
                        <span>{invoiceline.total_amount.toFixed(2)}</span>
                      </div>
                    </td>
                    <td className='text-nowrap col-3'>
                      <span className='d-block'>{invoiceline.total_tax_amount.toFixed(2)}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {issuerInvoicelines.length === 0 && (
            <div className='d-flex justify-content-center align-items-center h-50'>
              <span className='fw-bold fs-4'>{t('homePage.noInvoiceLines')}</span>
            </div>
          )}
        </div>
        <div className='col p-4 shadow-sm'>
          <div className='row mb-2 p-2'>
            <span
              className={`subtitle text-decoration-underline pb-2 ${
                i18n.language === 'en' ? '' : 'text-end'
              }`}
              style={{ textUnderlineOffset: '10px' }}>
              <strong className='fs-5'>{t('homePage.dashboardPage.goods')}</strong>
            </span>
          </div>
          <div className='row'>
            <table className='table col mt-2' dir={i18n.language === 'en' ? 'ltr' : 'rtl'}>
              <thead
                style={{
                  verticalAlign: 'middle',
                }}>
                <tr>
                  <th scope='col-4'>{t('homePage.dashboardPage.table.description')}</th>
                  <th scope='col-2'>{t('homePage.dashboardPage.table.count')}</th>
                  <th scope='col-3 text-nowrap'>{t('homePage.dashboardPage.table.total')}</th>
                  <th scope='col-3 text-nowrap'>{t('homePage.dashboardPage.table.tax')}</th>
                </tr>
              </thead>
              <tbody>
                {receiverInvoicelines.map(invoiceline => (
                  <tr key={invoiceline.id}>
                    <td className='col-4' dir='ltr'>
                      <span className='d-block fw-bold dashboard__truncatedText'>
                        {invoiceline.description}
                      </span>
                      <span className='d-block'>{invoiceline.item_code}</span>
                    </td>
                    <td className='col-2'>
                      <span className='d-block fw-bold'>{invoiceline.count}</span>
                    </td>
                    <td className='col-3'>
                      <div className='d-block'>
                        <span>{invoiceline.total_amount.toFixed(2)}</span>
                      </div>
                    </td>
                    <td className='text-nowrap col-3'>
                      <span className='d-block'>{invoiceline.total_tax_amount.toFixed(2)}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {receiverInvoicelines.length === 0 && (
            <div className='d-flex justify-content-center align-items-center h-50'>
              <span className='fw-bold fs-4'>{t('homePage.noInvoiceLines')}</span>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default Dashboard;
